import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { firebase } from '@firebase/app'
import '@firebase/storage'
import { v4 as uuid } from 'uuid'
import { makeStyles, IconButton, Button, Tooltip } from '@material-ui/core'
import { Description, CloudDownload } from '@material-ui/icons'
import { useSession } from 'components/session/session'
import { Checkboxes } from 'mui-rff'
import { OnChange } from 'react-final-form-listeners'
import { Form } from 'react-final-form'
// eslint-disable-next-line no-undef
const downloadjs = require('downloadjs')

const useStyles = makeStyles(() => ({
  button: {
    marginLeft: '20px'
  },
  thumbnail: {
    maxWidth: '100%',
    height: '150px',
    '& iframe': {
      border: 'none',
    }
  },
  full: {
    maxWidth: '100%',
    maxHeight: '400px',
    '& iframe': {
      border: 'none',
      width: '100%',
      height: '400px'
    }
  },
  thumbnailHolder: {
    '& button': {
      visibility: 'hidden'
    },
    '&:hover': {
      '& button': {
        visibility: 'visible'
      }
    }
  },
}))

export default function ImageComponent({ filepath, component, save, disable, live }) {
  const classes = useStyles()
  const session = useSession()
  const [uploadedFile, setUploadedFile] = useState(null)

  useEffect(() => {
    const renderFiles = async () => {
      if (!component.Prompt) return null
      const filename = component.Prompt.split('/').pop()
      const fileExists = (await firebase.storage().ref(component.Prompt.substr(0, component.Prompt.lastIndexOf('/'))).list()).items.filter(e => e.name === filename)
      if (fileExists.length > 0) {
        const fileRef = firebase.storage().ref(component.Prompt)
        fileRef.updateMetadata({ cacheControl: 'public,max-age=10000' })
        let url
        try { url = await fileRef.getDownloadURL() } catch (err) { console.error(err) }
        setUploadedFile(url)
      }
    }
    renderFiles()
  }, [component])

  const uploadFile = () => {
    const f = document.createElement('input')
    f.style.display = 'none'
    f.type = 'file'
    f.accept = '*'//'image/*,video/*,audio/*,application/pdf'
    document.body.appendChild(f)
    f.addEventListener('change', async e => {
      const rand = uuid()
      if (e.target.files[0]) {
        const file = e.target.files[0]
        const filename = `${filepath}/${rand}~~~${file.name}`
        const fileRef = firebase.storage().ref(filename)
        const uploadTask = fileRef.put(file)
        uploadTask.on('state_changed', snap => {
          const progress = Math.round((snap.bytesTransferred / (snap.totalBytes || 1)) * 100)
          session.snackbar.setSnackbar({ duration: null, message: `Uploading ${file.name} - ${progress}%` })
        })
        await uploadTask
        fileRef.updateMetadata({ cacheControl: 'public,max-age=10000' })
        let url
        try { url = await fileRef.getDownloadURL() } catch (err) { console.error(err) }
        setUploadedFile(url)
        session.snackbar.setSnackbar({ message: 'Upload Complete' })
        // delete old file
        try { await firebase.storage().ref(component.Prompt).delete() } catch (e) { console.log('File not found') }
        component.Prompt = filename
        save(component)
      }

      document.body.removeChild(f)
    })
    f.dispatchEvent(new MouseEvent('click'))
  }

  function File({ fileURI, disable, live }) {
    let content = <></>
    if (fileURI) {
      const filename = fileURI.split('?')[0].split('%2F').pop()
      const download = () => {
        const xhr = new XMLHttpRequest()
        xhr.responseType = 'blob'
        xhr.onprogress = event => {
          const progress = Math.round((event.loaded / (event.total || 1)) * 100)
          session.snackbar.setSnackbar({ duration: null, message: `Downloading ${filename} - ${progress}%` })

        }
        xhr.onload = () => {
          downloadjs(xhr.response, filename)
          session.snackbar.setSnackbar({ message: 'Download Complete' })
        }
        xhr.open('GET', fileURI)
        xhr.send()
      }

      const c = disable ? classes.thumbnail : classes.full
      let fileContentHolder
      if (filename.match(/.(jpg|jpeg|png|gif|webp|svg)$/i)) {
        fileContentHolder = (<img src={fileURI} className={c} />)
      } else if (filename.match(/.(mp4|ogg|webm)$/i)) {
        fileContentHolder = (<div className={c}><iframe className={c} srcDoc={`<video controls src="${fileURI}" style="width:100%;height:${disable ? 130 : 370}px" preload="none"/>`} /></div>)
      } else if (filename.match(/.(mp3|wav)$/i)) {
        fileContentHolder = (<audio controls src={fileURI} className={c} />)
      } else if (filename.match(/.(pdf)$/i)) {
        fileContentHolder = (<embed src={fileURI} type="application/pdf" className={c} style={disable ? {} : { width: '100%', height: '352px' }} />)
      } else {
        fileContentHolder = (<span><Description style={{ fontSize: 40 }} className={c} />{filename.split('~~~')?.[1] ?? ''}</span>)
      }
      content = <>
        {!disable && !live && <div className={classes.buttons}>
          {filename.match(/.(mp4|ogg|webm)$/i) && <Form
            initialValues={component}
            onSubmit={() => { }}
            render={({ values }) => <>
              <Tooltip title='For video content, Simplemnt will display the content in the native aspect ratio.' placement='top-start'><span>
                <Checkboxes name='PreserveVideoAspectRatio' variant='outlined' data={{ label: 'Use Native Aspect Ratio', value: true }} />
              </span></Tooltip>
              <OnChange name='PreserveVideoAspectRatio'>{() => {
                save(values)
              }}</OnChange>
            </>}
          />}
          <Tooltip title='Save File'><IconButton onClick={download} ><CloudDownload /></IconButton></Tooltip>
        </div>}
        <div style={{ marginTop: live ? '10px' : '0px' }}>
          {fileContentHolder}
        </div>
      </>
    }
    return (
      <div className={classes.thumbnailHolder}>
        <div style={{ height: disable ? '150px' : '400px' }} >
          {content}
        </div>
      </div>
    )
  }

  File.propTypes = {
    fileURI: PropTypes.string,
    disable: PropTypes.bool,
    live: PropTypes.bool
  }
  if (disable) return <File fileURI={uploadedFile} disable={true} />
  else {
    return <>
      <Button className={classes.button} variant='contained' color='secondary' onClick={uploadFile}>{live ? 'Change File' : 'Upload File'}</Button>
      <File fileURI={uploadedFile} live={live} />
    </>
  }
}

ImageComponent.propTypes = {
  filepath: PropTypes.string.isRequired,
  component: PropTypes.object.isRequired,
  save: PropTypes.func.isRequired,
  disable: PropTypes.bool,
  live: PropTypes.bool,
}